import { useCallback, useEffect, useRef, useState } from "react";
import { startGoogleOauth } from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";
import { useMintOrgId } from "components/main/connections/useMintOrg";
import { useConnectionsStore } from "stores/connectionsStore";
import { createLogger } from "debug/DebugingLogger";

const log = createLogger("useGoogleOauthPopup");

const POPUP_TIMEOUT_MS = 2 * 60 * 1000;

export type GoogleOauthOutcome =
  | { status: "ok"; saved_connection_id: string; account_email: string }
  | { status: "email_mismatch"; expected: string; received: string }
  | { status: "error"; message: string }
  | { status: "popup_blocked" }
  | { status: "cancelled" };

interface UsePopupResult {
  pending: boolean;
  start: (params?: {
    savedConnectionId?: string;
    clientType?: "sheets" | "bucket";
  }) => Promise<GoogleOauthOutcome>;
}

export const useGoogleOauthPopup = (): UsePopupResult => {
  const mintOrgId = useMintOrgId();
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);

  useEffect(() => {
    return () => {
      pendingRef.current = false;
    };
  }, []);

  const start = useCallback(
    async (params?: {
      savedConnectionId?: string;
      clientType?: "sheets" | "bucket";
    }): Promise<GoogleOauthOutcome> => {
      if (pendingRef.current) {
        log.warning("popup already pending; ignoring duplicate start");
        return { status: "cancelled" };
      }

      // Open the popup SYNCHRONOUSLY inside the click gesture. Safari (and
      // Firefox) block any window.open that happens after an `await`, so we
      // open a blank popup now and navigate it once the auth URL resolves.
      const popup = window.open(
        "about:blank",
        "google_oauth",
        "width=600,height=700",
      );
      if (!popup) {
        return { status: "popup_blocked" };
      }

      const csrf = crypto.randomUUID();
      let auth_url: string;
      try {
        ({ auth_url } = await startGoogleOauth({
          csrf,
          savedConnectionId: params?.savedConnectionId,
          clientType: params?.clientType,
          // Only meaningful for a net-new source; the server ignores it on the
          // reauthorize path, which inherits the existing row's org (D24b).
          orgId: mintOrgId,
        }));
      } catch (e) {
        popup.close();
        log.error("failed to mint google oauth url: %o", e);
        return { status: "error", message: "Failed to start Google sign-in" };
      }
      popup.location.href = auth_url;

      pendingRef.current = true;
      setPending(true);

      return new Promise<GoogleOauthOutcome>((resolve) => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        let closeWatcher: ReturnType<typeof setInterval> | null = null;
        let channel: BroadcastChannel | null = null;
        const processData = (
          data:
            | {
                type?: string;
                status?: string;
                state?: string;
                saved_connection_id?: string;
                account_email?: string;
                expected_email?: string;
                received_email?: string;
                message?: string;
              }
            | undefined,
        ) => {
          if (!data || data.type !== "google_oauth_done") return;
          if (data.status === "ok" && data.state !== csrf) {
            resolve({ status: "error", message: "CSRF mismatch" });
            cleanup();
            return;
          }
          let outcome: GoogleOauthOutcome;
          if (data.status === "ok") {
            outcome = {
              status: "ok",
              saved_connection_id: data.saved_connection_id ?? "",
              account_email: data.account_email ?? "",
            };
          } else if (data.status === "email_mismatch") {
            outcome = {
              status: "email_mismatch",
              expected: data.expected_email ?? "",
              received: data.received_email ?? "",
            };
          } else {
            outcome = {
              status: "error",
              message: data.message ?? "OAuth failed",
            };
          }
          resolve(outcome);
          cleanup();
        };
        // postMessage path — works when the opener link survives.
        // Guarded by origin since postMessage can come from anywhere.
        const handler = (ev: MessageEvent) => {
          if (ev.origin !== window.location.origin) return;
          processData(ev.data);
        };
        // BroadcastChannel path — same-origin by construction, so it
        // delivers when window.opener is severed (e.g. Safari).
        const channelHandler = (ev: MessageEvent) => processData(ev.data);
        const cleanup = () => {
          window.removeEventListener("message", handler);
          if (channel) {
            channel.removeEventListener("message", channelHandler);
            channel.close();
          }
          if (timer) clearTimeout(timer);
          if (closeWatcher) clearInterval(closeWatcher);
          pendingRef.current = false;
          setPending(false);
        };
        window.addEventListener("message", handler);
        try {
          channel = new BroadcastChannel("google_oauth_channel");
          channel.addEventListener("message", channelHandler);
        } catch {
          channel = null;
        }
        // Watchdog — last-resort timeout if neither postMessage nor close-poll fires.
        timer = setTimeout(() => {
          resolve({ status: "cancelled" });
          cleanup();
        }, POPUP_TIMEOUT_MS);
        // Browsers don't fire an event on popup-close — poll. 300ms grace
        // lets a racing success postMessage land before we cancel.
        closeWatcher = setInterval(() => {
          if (!popup.closed) return;
          if (closeWatcher) {
            clearInterval(closeWatcher);
            closeWatcher = null;
          }
          setTimeout(() => {
            if (!pendingRef.current) return;
            resolve({ status: "cancelled" });
            cleanup();
          }, 300);
        }, 500);
      });
    },
    [mintOrgId],
  );

  return { pending, start };
};

export const useAddGoogleConnection = () => {
  const { start, pending } = useGoogleOauthPopup();
  const refetch = useConnectionsStore((s) => s.refetch);
  return {
    pending,
    start: async (params?: {
      savedConnectionId?: string;
      clientType?: "sheets" | "bucket";
    }) => {
      const outcome = await start(params);
      if (outcome.status === "ok") {
        await refetch();
      }
      return outcome;
    },
  };
};

/** Reauthorize a Google connection with the Sheets scope set (drive.file +
 *  userinfo). On `ok`, refetches connections so the new scopes show up on
 *  the stored credential before the caller retries the Picker. */
export const useAuthorizeSheets = () => {
  const { start, pending } = useGoogleOauthPopup();
  const refetch = useConnectionsStore((s) => s.refetch);
  return {
    pending,
    authorize: async (
      savedConnectionId: string,
    ): Promise<GoogleOauthOutcome> => {
      const outcome = await start({ savedConnectionId, clientType: "sheets" });
      if (outcome.status === "ok") {
        await refetch();
      }
      return outcome;
    },
  };
};
